import {
  paymentMethodOnFileAtom,
  selectedPaymentMethodAtom,
} from '@openeducation/pp-rn-jotai-atoms';
import {
  PaymentInfoProps,
  PaymentMethodOnFile,
  PaymentMethod,
  CreditCard,
  Subscription,
} from '@openeducation/pp-rn-shared-types';
import type { ExtendedPaymentInfoFormData } from '@openeducation/pp-rn-shared-types';
import { useAtomValue, useAtom } from 'jotai';
import React, { useEffect, useCallback } from 'react';

interface UsePaymentMethodOnFileProps {
  isUpsell: boolean;
  subscription?: Subscription;
  setFormData: React.Dispatch<
    React.SetStateAction<ExtendedPaymentInfoFormData>
  >;
}

export const usePaymentMethodOnFile = ({
  isUpsell,
  subscription,
  setFormData,
}: UsePaymentMethodOnFileProps) => {
  const [selectedPaymentMethodOnFile, setSelectedPaymentMethodOnFile] = useAtom(
    paymentMethodOnFileAtom
  );
  const selectedPaymentMethod = useAtomValue(selectedPaymentMethodAtom);
  const subscriptionPaymentMethod = subscription?.paymentMethod;
  const creditCard = subscription?.creditCard;

  const isEnabled =
    selectedPaymentMethod === subscriptionPaymentMethod &&
    isUpsell &&
    !!subscription;

  const setExistingPaymentMethodValues = useCallback(() => {
    setFormData((prev: ExtendedPaymentInfoFormData) => ({
      ...prev,
      paymentMethod: (subscriptionPaymentMethod || '') as PaymentMethod,
      card1_cardType: (creditCard?.type || '') as CreditCard,
      card1_cardNumber: creditCard?.number || '',
      card1_expirationMonth: '',
      card1_expirationYear: '',
      card1_holderName: creditCard?.holderName || '',
      card1_cvv: '',
      card1_installments: '',
      card2_cardType: '' as CreditCard,
      card2_cardNumber: '',
      card2_expirationMonth: '',
      card2_expirationYear: '',
      card2_holderName: '',
      card2_cvv: '',
      card2_installments: '',
      card2_amount: '',
      card2_billingAddress_address1: '',
      card2_billingAddress_city: '',
      card2_billingAddress_postcode: '',
      card2_billingAddress_country: '',
      card2_documentNumber: '',
      card2_documentType: '',
    }));
  }, [setFormData, subscriptionPaymentMethod, creditCard]);

  const setNewPaymentMethodValues = useCallback(() => {
    setFormData((prev: ExtendedPaymentInfoFormData) => ({
      ...prev,
      card1_cardType: '' as CreditCard,
      card1_cardNumber: '',
      card1_expirationMonth: '',
      card1_expirationYear: '',
      card1_holderName: '',
      card1_cvv: '',
      card1_installments: '',
      card2_cardType: '' as CreditCard,
      card2_cardNumber: '',
      card2_expirationMonth: '',
      card2_expirationYear: '',
      card2_holderName: '',
      card2_cvv: '',
      card2_installments: '',
    }));
  }, [setFormData]);

  useEffect(() => {
    if (isEnabled) {
      setSelectedPaymentMethodOnFile(PaymentMethodOnFile.EXISTING);
    } else {
      setSelectedPaymentMethodOnFile(PaymentMethodOnFile.NEW);
      setNewPaymentMethodValues();
    }
  }, [isEnabled, setSelectedPaymentMethodOnFile, setNewPaymentMethodValues]);

  useEffect(() => {
    if (isEnabled) {
      if (selectedPaymentMethodOnFile === PaymentMethodOnFile.EXISTING) {
        setExistingPaymentMethodValues();
      }
      if (selectedPaymentMethodOnFile === PaymentMethodOnFile.NEW) {
        setNewPaymentMethodValues();
      }
    }
  }, [
    setExistingPaymentMethodValues,
    setNewPaymentMethodValues,
    isEnabled,
    selectedPaymentMethodOnFile,
  ]);

  const paymentMethodOnFile: PaymentInfoProps['paymentMethodOnFile'] = {
    isEnabled,
    value: selectedPaymentMethodOnFile,
    onChange: (value: PaymentMethodOnFile) =>
      setSelectedPaymentMethodOnFile(value),
  };

  return {
    paymentMethodOnFile,
    isExistingPaymentMethod:
      selectedPaymentMethodOnFile === PaymentMethodOnFile.EXISTING,
  };
};
